import { useDisclosure, useToast } from "@chakra-ui/react";
import { db } from "connections/firebase";
import {
  updateDoc,
  deleteDoc,
  QueryDocumentSnapshot,
  SnapshotOptions,
  WithFieldValue,
  DocumentData,
  collection,
  doc,
  FirestoreDataConverter,
  addDoc,
} from "firebase/firestore";
import _ from "lodash";
import { FormikHelpers } from "formik";
import { IHouseType, IHouseTypeFormPayload } from "interfaces/houseType";
import { useEffect, useState } from "react";
import { useCollectionData } from "react-firebase-hooks/firestore";

const converter: FirestoreDataConverter<IHouseType> = {
  toFirestore(houseType: WithFieldValue<IHouseType>): DocumentData {
    return houseType;
  },
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions
  ): IHouseType {
    const data = snapshot.data(options);
    return {
      nama: data.nama,
      luas: data.luas,
      id: snapshot.id,
    };
  },
};
const initialValues: IHouseTypeFormPayload = {
  nama: "",
  luas: "",
};

const collection_name = "houseType";

export default function useHouseType() {
  const toast = useToast();
  const [data, loading, error] = useCollectionData(
    collection(db, collection_name).withConverter(converter),
    {
      snapshotListenOptions: { includeMetadataChanges: true },
    }
  );
  const { onClose: onCloseForm, onOpen, isOpen: isOpenForm } = useDisclosure();

  const [formValue, setFormValue] = useState<{
    id: string;
    initialValues: IHouseTypeFormPayload;
  }>({
    id: "",
    initialValues,
  });

  const [loadingAction, setLoadingAction] = useState<null | string>(null);

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [error, toast]);

  const openEditForm = (id: string) => {
    onOpen();
    const detail = getHouseTypeDetail(id);
    const initialValues = _.omit(detail, ["id"]);
    setFormValue({
      id,
      initialValues,
    });
  };

  const openAddForm = () => {
    onOpen();
    setFormValue({
      id: "new",
      initialValues,
    });
  };

  const getHouseTypeDetail = (id: string) => {
    if (data) {
      const selected = data.find((value) => value.id === id);
      if (selected) return selected;
    }

    return null;
  };

  const addHouseType = async (params: IHouseTypeFormPayload) => {
    setLoadingAction("new");
    await addDoc(collection(db, collection_name), params);
    setLoadingAction(null);
  };

  const updateHouseType = async (id: string, params: IHouseTypeFormPayload) => {
    setLoadingAction(id);
    await updateDoc(doc(db, collection_name, id), params);
    setLoadingAction(null);
  };

  const deleteHouseType = async (id: string) => {
    setLoadingAction(id);
    await deleteDoc(doc(db, collection_name, id));
    setLoadingAction(null);
  };

  const onSubmitForm = async (
    values: IHouseTypeFormPayload,
    formikHelpers: FormikHelpers<IHouseTypeFormPayload>
  ) => {
    if (formValue.id === "new") {
      await addHouseType(values);
    } else {
      await updateHouseType(formValue.id, values);
    }

    onCloseForm();
    formikHelpers.resetForm();
  };

  return {
    data,
    loading,
    addHouseType,
    updateHouseType,
    deleteHouseType,
    getHouseTypeDetail,
    openAddForm,
    openEditForm,
    isLoadingAction: loadingAction !== null,
    formValue,
    isOpenForm,
    onCloseForm,
    onSubmitForm,
  };
}
